import React, { useState } from 'react';
import {
  Box, Button, Tooltip, Popover, Typography, Divider,
  FormControlLabel, Checkbox, Chip,
} from '@mui/material';
import {
  ViewColumn as ViewColumnIcon,
  RestartAlt as ResetIcon,
  SelectAll as SelectAllIcon,
} from '@mui/icons-material';
import { ALL_COLUMNS, ColumnId, UseColumnVisibilityReturn } from '../hooks/useColumnVisibility';

type Props = Pick<UseColumnVisibilityReturn, 'visibleColumns' | 'toggle' | 'showAll' | 'reset'>;

/**
 * Кнопка «Колонки» с поповером-меню для скрытия/показа столбцов таблицы.
 * Обязательные колонки показываются с отключённым чекбоксом.
 * Стилизована под стандартный элемент вкладки (Tab) для интеграции в панель табов.
 */
const ColumnVisibilityMenu: React.FC<Props> = ({ visibleColumns, toggle, showAll, reset }) => {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const open = Boolean(anchor);

  const hiddenCount = ALL_COLUMNS.filter(c => !c.required && !visibleColumns.has(c.id)).length;

  return (
    <>
      <Tooltip title="Управление видимостью колонок">
        <Button
          color="inherit"
          startIcon={<ViewColumnIcon sx={{ fontSize: 18 }} />}
          onClick={e => setAnchor(e.currentTarget)}
          sx={{
            height: 44,
            textTransform: 'none',
            fontWeight: 700,
            fontSize: '0.9rem',
            color: 'text.secondary',
            borderRadius: 0,
            px: 2,
            minWidth: 'auto',
            '&:hover': {
              color: 'primary.main',
              bgcolor: 'transparent',
            },
          }}
        >
          Колонки
          {hiddenCount > 0 && (
            <Chip
              label={`−${hiddenCount}`}
              size="small"
              color="primary"
              sx={{ ml: 0.75, height: 18, fontSize: '0.68rem', fontWeight: 700 }}
            />
          )}
        </Button>
      </Tooltip>

      <Popover
        open={open}
        anchorEl={anchor}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{
          elevation: 3,
          sx: { borderRadius: 2, p: 1.5, minWidth: 220, maxHeight: 480 },
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
          <Typography variant="subtitle2" fontWeight={700} sx={{ fontSize: '0.8rem' }}>
            Видимость колонок
          </Typography>
          <Box sx={{ display: 'flex', gap: 0.5 }}>
            <Tooltip title="Показать все">
              <Button size="small" onClick={showAll} sx={{ minWidth: 0, p: 0.5 }}>
                <SelectAllIcon sx={{ fontSize: 16 }} />
              </Button>
            </Tooltip>
            <Tooltip title="Сбросить к настройкам по умолчанию">
              <Button size="small" onClick={reset} sx={{ minWidth: 0, p: 0.5 }}>
                <ResetIcon sx={{ fontSize: 16 }} />
              </Button>
            </Tooltip>
          </Box>
        </Box>

        <Divider sx={{ mb: 1 }} />

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
          {ALL_COLUMNS.map(col => (
            <FormControlLabel
              key={col.id}
              control={
                <Checkbox
                  size="small"
                  checked={visibleColumns.has(col.id)}
                  onChange={() => toggle(col.id as ColumnId)}
                  disabled={col.required}
                  sx={{ py: 0.25, pl: 0.5, pr: 0.75 }}
                />
              }
              label={
                <Typography
                  variant="body2"
                  sx={{
                    fontSize: '0.8rem',
                    color: col.required ? 'text.disabled' : 'text.primary',
                  }}
                >
                  {col.label}
                  {col.required && (
                    <Typography component="span" variant="caption" sx={{ ml: 0.5, color: 'text.disabled', fontSize: '0.68rem' }}>
                      (обяз.)
                    </Typography>
                  )}
                </Typography>
              }
              sx={{ mx: 0, my: 0 }}
            />
          ))}
        </Box>
      </Popover>
    </>
  );
};

export default ColumnVisibilityMenu;
